import _get from 'lodash/get'
import { z } from 'zod'

import type { Language } from '@nxt-insurance/constants'
import { SupportedMarketKey } from '@nxt-insurance/constants'
import { IntlFormatter } from '@nxt-insurance/formatting'

import { buildPaymentStep } from '@/app/utils/flows/paymentStep'
import {
  buildAlreadyPurchasedStep,
  buildFailureStep,
  buildProgressBarSections,
  buildSuccessStep,
} from '@/app/utils/flows/sharedFlowSteps'
import { getApiService } from '@/services/api'
import { flowConfigsDe } from '@flows/configs'
import { getCommonFlowConfig } from '@flows/configs/common'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

const PRODUCT_KEY = 'contents_v1_getsafe_de' as const
const I18N_NAMESPACE = 'sharedFlows_contentsNxt' as const
const CONTENTS_FIRST_STEP_NAME = 'contents_instant' as const

// lodash _get prefers flat dotted keys over nested paths when both exist.
// The onSubmit hooks store values as flat keys (e.g. 'products.x.y': true),
// so _get returns the stale flat-key value instead of the nested form value.
const getConfigParam = (flowValues: FlowValues, param: string): unknown =>
  _get(flowValues, ['products', PRODUCT_KEY, 'configuration_parameters', param])

export const getContentsFlowConfig = (
  language: Language,
  req?: Parameters<typeof getApiService>[0]['req'],
  res?: Parameters<typeof getApiService>[0]['res']
) => {
  const formatter = new IntlFormatter({
    getCurrentLanguage: () => language,
    getCurrentMarket: () => SupportedMarketKey.De,
  })
  const base = flowConfigsDe.contentsNxt({
    clientHelpers: {
      apiService: getApiService({ language, req, res }),
      getMarket: () => SupportedMarketKey.De,
      formatter,
    },
  })

  const apiService = getApiService({ language, req, res })
  const common = getCommonFlowConfig({
    getMarket: () => SupportedMarketKey.De,
    apiService,
  })

  const baseSteps = base.getSteps()
  const baseOptions = base.getOptions()
  const { nextStep: _omitNext, ...instantWithoutNext } = baseSteps.contents_instant

  const contents_instant = {
    ...instantWithoutNext,
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        const numberOfClaimsValue = getConfigParam(flowValues, 'number_of_claims_in_last_5y')
        if (numberOfClaimsValue === 3) {
          return 'failure'
        }
        return 'contents_quote'
      },
      valueToStepName: {
        contents_quote: 'contents_quote',
        failure: 'failure',
      },
    },
  } as const satisfies AnyStepConfiguration

  const { nextStep: _omitQuoteNext, ...baseQuoteStep } = baseSteps.contents_quote
  const contents_quote = {
    ...baseQuoteStep,
    showOrderSummary: true,
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        const previouslyOwnedValue = getConfigParam(flowValues, 'previous_insurance_in_last_5y')
        return previouslyOwnedValue
          ? 'contents_insurance_cancelled'
          : 'contents_permanent_residence'
      },
      valueToStepName: {
        contents_insurance_cancelled: 'contents_insurance_cancelled',
        contents_permanent_residence: 'contents_permanent_residence',
      },
    },
    schema: baseSteps.contents_quote.schema.and(
      z.object({ startDates: z.object({ [PRODUCT_KEY]: z.record(z.string()) }) })
    ),
  } as const satisfies AnyStepConfiguration

  const { nextStepConfig: _omitCancelledNextConfig, ...baseCancelled } =
    baseSteps.contents_insurance_cancelled
  const contents_insurance_cancelled = {
    ...baseCancelled,
    showOrderSummary: true,
    subFlow: SubFlow.Common,
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        return getConfigParam(flowValues, 'cancelled_by_previous_insurer')
          ? 'failure'
          : 'contents_permanent_residence'
      },
      valueToStepName: {
        contents_permanent_residence: 'contents_permanent_residence',
        failure: 'failure',
      },
    },
  } as const satisfies AnyStepConfiguration

  const contents_permanent_residence = {
    ...baseSteps.contents_permanent_residence,
    showOrderSummary: true,
    subFlow: SubFlow.Common,
    nextStepConfig: {
      valueFunction: (flowValues: FlowValues) => {
        return getConfigParam(flowValues, 'permanent_residence_in_germany') === true
          ? 'contents_personal_details'
          : 'failure'
      },
      valueToStepName: {
        contents_personal_details: 'contents_personal_details',
        failure: 'failure',
      },
    },
  } as const satisfies AnyStepConfiguration

  const contents_personal_details = {
    ...baseSteps.contents_personal_details,
    showOrderSummary: true,
    nextStep: 'gender',
  } as const satisfies AnyStepConfiguration

  const gender = {
    ...common.gender,
    showOrderSummary: true,
    nextStep: 'payment',
  } as const satisfies AnyStepConfiguration

  const payment = buildPaymentStep(I18N_NAMESPACE)

  return {
    getSteps: () => ({
      contents_instant,
      contents_quote,
      contents_insurance_cancelled,
      contents_permanent_residence,
      contents_personal_details,
      gender,
      payment,
      already_purchased: buildAlreadyPurchasedStep(),
      success: buildSuccessStep(),
      failure: buildFailureStep(I18N_NAMESPACE),
    }),
    getOptions: () => ({
      ...baseOptions,
      constants: {
        ...baseOptions.constants,
        FIRST_STEP_NAME: CONTENTS_FIRST_STEP_NAME,
        flowName: 'contents',
        productKey: PRODUCT_KEY,
      },
      progressBarSections: buildProgressBarSections({
        insuranceSubFlow: SubFlow.ContentsNxt,
        i18nNamespace: I18N_NAMESPACE,
      }),
    }),
  }
}
